import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0, left: 0 };
  },
});

const PUBLIC_PATHS = ['/login', '/register', '/forgot-password', '/otp', '/resetpassword'];

router.beforeEach((routeTo, routeFrom, next) => {
  const isPublic = PUBLIC_PATHS.includes(routeTo.path);
  const token    = localStorage.getItem('ENSPSID');

  // 1. No token on a protected page → go to login
  if (!isPublic && !token) {
    return next({ name: 'login', query: { redirectFrom: routeTo.fullPath } });
  }

  // 2. Unknown route → 404
  if (routeTo.matched.length === 0) {
    return next({ name: '404' });
  }

  // 3. Public pages pass through
  if (isPublic) return next();

  // 4. Permission check — only runs if permissions are already loaded in the store.
  //    App.vue dispatches getProfile on startup; on hard refresh the first navigation
  //    is always allowed, and subsequent ones are fully guarded once the store is ready.
  const required = routeTo.meta && routeTo.meta.requiredPermission;
  if (required) {
    const perms = store.getters['authfack/get_user_permissions'];
    if (perms && perms.length > 0) {
      // Super Admin bypasses every permission check
      if (store.getters['authfack/hasRole']('Super Admin')) {
        return next();
      }
      if (!store.getters['authfack/hasPermission'](required)) {
        return next({ name: '403' });
      }
    }
    // Permissions not loaded yet → let through; App.vue will populate the store
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            if (args.length) { next(...args); reject(new Error('Redirected')); }
            else { resolve(); }
          });
        } else {
          resolve();
        }
      });
    }
  } catch {
    // A beforeResolve hook redirected — navigation was already forwarded
    return;
  }

  const title = routeTo.meta && routeTo.meta.title;
  document.title = (title || 'Page') + ' | ' + appConfig.title;
  next();
});

export default router;
